import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "providers";
import { Formik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { URL } from "api/pesabackend";

const loginSchema = yup.object().shape({
  username: yup.string().required("Username is required"),
  password: yup.string().required("Password is required"),
});

const initialValuesLogin = {
  username: "",
  password: "",
};

const styles = {
  wrap: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem 1rem",
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: "420px",
    backgroundColor: "#ffffff",
    border: "0.5px solid #e2e2e2",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  header: {
    padding: "2rem 2rem 1.5rem",
    borderBottom: "0.5px solid #e2e2e2",
  },
  eyebrow: {
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#999",
    margin: "0 0 8px",
  },
  title: {
    fontFamily: "'DM Serif Display', serif",
    fontSize: "28px",
    fontWeight: 400,
    color: "#111",
    margin: 0,
    lineHeight: 1.25,
  },
  titleEm: {
    fontStyle: "italic",
    color: "#888",
  },
  body: {
    padding: "1.75rem 2rem 2rem",
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 14px",
    marginBottom: "1.25rem",
    backgroundColor: "#fff5f5",
    border: "0.5px solid #fca5a5",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#b91c1c",
  },
  errorDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "currentColor",
    flexShrink: 0,
  },
  fieldGroup: {
    marginBottom: "1.25rem",
  },
  label: {
    display: "block",
    fontSize: "12px",
    fontWeight: 500,
    color: "#666",
    marginBottom: "6px",
    letterSpacing: "0.04em",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 14px",
    fontSize: "14px",
    fontFamily: "'DM Sans', sans-serif",
    color: "#111",
    backgroundColor: "#f9f9f9",
    border: "0.5px solid #e2e2e2",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.15s, background 0.15s",
  },
  inputError: {
    borderColor: "#fca5a5",
    backgroundColor: "#fff5f5",
  },
  fieldError: {
    fontSize: "11px",
    color: "#b91c1c",
    marginTop: "4px",
  },
  submitBtn: {
    width: "100%",
    padding: "11px 16px",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "'DM Sans', sans-serif",
    letterSpacing: "0.04em",
    color: "#111",
    backgroundColor: "#ffffff",
    border: "0.5px solid #ccc",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    marginTop: "1.5rem",
    transition: "background 0.15s, transform 0.1s",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "1.25rem",
  },
  dividerLine: {
    flex: 1,
    height: "0.5px",
    backgroundColor: "#e2e2e2",
  },
  dividerText: {
    fontSize: "11px",
    color: "#bbb",
  },
};

const Spinner = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    style={{ animation: "spin 0.6s linear infinite" }}
  >
    <circle
      cx="7" cy="7" r="5"
      fill="none"
      stroke="#ccc"
      strokeWidth="1.5"
    />
    <path
      d="M7 2 A5 5 0 0 1 12 7"
      fill="none"
      stroke="#111"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </svg>
);

const Form = () => {
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const login = async (values, onSubmitProps) => {
    setLoading(true);
    setLoginError(false);
    try {
      const response = await axios.post(`${URL}/accounts/login`, values);
      onSubmitProps.resetForm();
      if (response.status === 200) {
        const { user_details, token } = response.data.data;
        authLogin({
          user: user_details,
          token: token.access,
          refresh: token.refresh,
          business_id: user_details.related_business,
          business_branch_id: user_details.related_business_branch.id,
          business_branch_details: user_details.related_business_branch,
          permissions: response.data.data.permissions.permissions,
          modules: response.data.data.permissions.modules,
        });
        navigate("/dashboards");
      }
    } catch {
      setLoginError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500&display=swap"
        rel="stylesheet"
      />
      <div style={styles.wrap}>
        <div style={styles.card}>
          <div style={styles.header}>
            <p style={styles.eyebrow}>Welcome back</p>
            <h1 style={styles.title}>
              Sign <em style={styles.titleEm}>in to</em>
              <br />
              your account
            </h1>
          </div>

          <div style={styles.body}>
            {loginError && (
              <div style={styles.errorBanner}>
                <span style={styles.errorDot} />
                Invalid username or password. Please try again.
              </div>
            )}

            <Formik
              onSubmit={login}
              initialValues={initialValuesLogin}
              validationSchema={loginSchema}
            >
              {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label} htmlFor="username">Username</label>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      placeholder="your.username"
                      autoComplete="username"
                      value={values.username}
                      onChange={handleChange}
                      onBlur={(e) => { handleBlur(e); setFocusedField(null); }}
                      onFocus={() => setFocusedField("username")}
                      style={{
                        ...styles.input,
                        ...(touched.username && errors.username ? styles.inputError : {}),
                        ...(focusedField === "username"
                          ? { borderColor: "#888", backgroundColor: "#fff", boxShadow: "0 0 0 3px rgba(0,0,0,0.06)" }
                          : {}),
                      }}
                    />
                    {touched.username && errors.username && (
                      <p style={styles.fieldError}>{errors.username}</p>
                    )}
                  </div>

                  <div style={styles.fieldGroup}>
                    <label style={styles.label} htmlFor="password">Password</label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      autoComplete="current-password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={(e) => { handleBlur(e); setFocusedField(null); }}
                      onFocus={() => setFocusedField("password")}
                      style={{
                        ...styles.input,
                        ...(touched.password && errors.password ? styles.inputError : {}),
                        ...(focusedField === "password"
                          ? { borderColor: "#888", backgroundColor: "#fff", boxShadow: "0 0 0 3px rgba(0,0,0,0.06)" }
                          : {}),
                      }}
                    />
                    {touched.password && errors.password && (
                      <p style={styles.fieldError}>{errors.password}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      ...styles.submitBtn,
                      ...(loading ? { opacity: 0.6, cursor: "not-allowed" } : {}),
                    }}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = "#f5f5f5"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#ffffff"; }}
                    onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
                    onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                  >
                    {loading ? <Spinner /> : null}
                    {loading ? "Signing in..." : "Continue"}
                  </button>
                </form>
              )}
            </Formik>

            <div style={styles.divider}>
              <span style={styles.dividerLine} />
              <span style={styles.dividerText}>Secure login</span>
              <span style={styles.dividerLine} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Form;